import { create } from 'zustand';
import { AIService } from 'src/services/ai';
import {
  EstimationRequest,
  EstimationResponse,
  InsuranceChatRequest,
  InsuranceChatResponse,
} from 'src/services/interfaces/ai';
import { useApplicationStore } from 'src/store/application';

interface AIState {
  // Estimation state
  estimationLoading: boolean;
  estimationResult: EstimationResponse | null;
  estimationError: string | null;

  // Chat state
  chatLoading: boolean;
  chatSessionId: string | null;
  chatHistory: InsuranceChatResponse[];
  chatError: string | null;
  currentPage: string | null;

  // Plan change tracking
  isChangeAfterEstimation: boolean;

  // Actions
  runEstimation: (data: EstimationRequest) => Promise<void>;
  sendChatMessage: (data: InsuranceChatRequest, application: any) => Promise<void>;
  generateNewSessionId: () => void;
  clearEstimation: () => void;
  clearChat: () => void;
  clearErrors: () => void;
  setCurrentPage: (page: string) => void;
  setChangeAfterEstimation: (changed: boolean) => void;
}

export const useAIStore = create<AIState>((set, get) => ({
  // Initial state
  estimationLoading: false,
  estimationResult: null,
  estimationError: null,

  chatLoading: false,
  chatSessionId: null,
  chatHistory: [],
  chatError: null,
  currentPage: null,

  isChangeAfterEstimation: false,

  // Actions
  runEstimation: async (data: EstimationRequest) => {
    set({ estimationLoading: true, estimationError: null });

    try {
      const result = await AIService.runEstimation(data);
      set({ 
        estimationResult: result, 
        estimationLoading: false 
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to run estimation';
      set({ 
        estimationError: errorMessage,
        estimationLoading: false 
      });
    }
  },

  sendChatMessage: async (data: InsuranceChatRequest, application: any) => {
    set({ chatLoading: true, chatError: null });

    try {
      // Generate session_id if not provided
      const currentState = get();
      const sessionId = data.session_id || currentState.chatSessionId || crypto.randomUUID();
      // const { applications } = useApplicationStore.getState();

      // Prepare request data with session_id and current page context
      const requestData: InsuranceChatRequest = {
        ...data,
        session_id: sessionId,
        context: currentState.currentPage ? {
          page: currentState.currentPage,
          applicationDTO: btoa(JSON.stringify(application)), // Encode application to base64
          // application
        } : undefined,
      };

      const response = await AIService.sendInsuranceChatMessage(requestData);

      // Create a chat history item with both the user message and assistant response
      const chatHistoryItem: InsuranceChatResponse = {
        session_id: response.session_id || sessionId,
        message: data.message, // The user's message
        answer: response.answer, // The assistant's response
        sources: response.sources || [],
        hooks: response.hooks || [],
      };

      set((state) => ({
        chatHistory: [...state.chatHistory, chatHistoryItem],
        chatSessionId: response.session_id || sessionId,
        chatLoading: false,
      }));

      // Обрабатываем хуки из ответа
      if (response.hooks && response.hooks.length > 0) {
        // Эмитируем событие для обработки хуков
        window.dispatchEvent(new CustomEvent('ai-hooks', { 
          detail: { hooks: response.hooks } 
        }));
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send chat message';
      set({ 
        chatError: errorMessage,
        chatLoading: false 
      });
    }
  },

  generateNewSessionId: () => {
    set({ chatSessionId: crypto.randomUUID() });
  },

  clearEstimation: () => {
    set({ 
      estimationResult: null, 
      estimationError: null 
    });
  },

  clearChat: () => {
    set({ 
      chatHistory: [], 
      chatSessionId: null, 
      chatError: null 
    });
  },

  clearErrors: () => {
    set({ 
      estimationError: null, 
      chatError: null 
    });
  },

  setCurrentPage: (page: string) => {
    set({ currentPage: page });
  },

  setChangeAfterEstimation: (changed: boolean) => {
    set({ isChangeAfterEstimation: changed });
  },
}));
